import { Injectable, signal } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export type AuthResult = 'restored' | 'dismissed';

/**
 * Coordinates the session-expiry re-auth flow.
 *
 * Lifecycle:
 *  1. Interceptor gets 401 → calls onUnauthorized()
 *  2. Service locks the app (isLocked = true), snapshots unsaved component state to localStorage
 *  3. AppComponent watches isLocked and shows the global auth modal
 *  4. On success  → onAuthRestored()  → authResult$ emits 'restored'  → interceptor retries
 *  5. On dismiss  → onAuthDismissed() → authResult$ emits 'dismissed' → interceptor returns the original error
 */
@Injectable({ providedIn: 'root' })
export class SessionStateService {
  // True while waiting for the user to re-authenticate
  readonly isLocked = signal(false);

  // Fires once when the re-auth flow completes (either way)
  private readonly authResultSubject = new Subject<AuthResult>();
  readonly authResult$: Observable<AuthResult> = this.authResultSubject.asObservable();

  // Registry of { key → snapshot function } registered by active editor components
  private readonly savers = new Map<string, () => unknown>();

  // ── Called by the interceptor on the first 401 in a locked-out cycle ───────

  onUnauthorized(): void {
    if (this.isLocked()) return; // idempotent — first 401 wins
    this.preserveAll();
    this.isLocked.set(true);
  }

  // ── Called by AppComponent's modal output bindings ──────────────────────────

  onAuthRestored(): void {
    this.isLocked.set(false);
    this.authResultSubject.next('restored');
  }

  onAuthDismissed(): void {
    this.isLocked.set(false);
    this.authResultSubject.next('dismissed');
  }

  // ── State-preservation registry (opt-in per component) ──────────────────────

  /**
   * Register a snapshot function for a component with unsaved state.
   * Returns an unregister callback — call it in ngOnDestroy.
   *
   * @param key    Unique localStorage key suffix, e.g. 'invoice-editor'
   * @param saver  Returns the current form state as a plain serializable object
   */
  register(key: string, saver: () => unknown): () => void {
    this.savers.set(key, saver);
    return () => this.savers.delete(key);
  }

  /**
   * Read back a previously snapshotted state (call in ngOnInit after page reloads).
   * Returns null if nothing was saved.
   */
  restore<T>(key: string): T | null {
    const raw = localStorage.getItem(this.storageKey(key));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  /** Clear a snapshot after it has been consumed. */
  clear(key: string): void {
    localStorage.removeItem(this.storageKey(key));
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private preserveAll(): void {
    for (const [key, saver] of this.savers) {
      try {
        const data = saver();
        if (data !== undefined && data !== null) {
          localStorage.setItem(this.storageKey(key), JSON.stringify(data));
        }
      } catch {
        // Ignore serialisation errors (e.g. circular refs from File objects)
      }
    }
  }

  private storageKey(key: string): string {
    return `inexflo_session_${key}`;
  }
}
